import React from "react";

/** Copies each column header onto its cells as ``data-label`` so a phone can
 * show every row as a labelled card. */
function labelCells(table: HTMLTableElement) {
  const heads = [
    ...table.querySelectorAll<HTMLElement>("thead tr:first-child > th"),
  ].map((th) => {
    const copy = th.cloneNode(true) as HTMLElement;
    copy.querySelectorAll("[role=tooltip]").forEach((t) => t.remove());
    return th.getAttribute("aria-label") ?? copy.textContent?.trim() ?? "";
  });
  table
    .querySelectorAll("thead th")
    .forEach((th) => th.setAttribute("scope", "col"));
  for (const row of table.querySelectorAll("tbody tr, tfoot tr")) {
    let col = 0;
    for (const cell of row.children) {
      const span = (cell as HTMLTableCellElement).colSpan || 1;
      if (span === 1 && heads[col]) cell.setAttribute("data-label", heads[col]);
      col += span;
    }
  }
}

/** The one data table: 12px muted header, 52px rows, hover, numeric columns
 * right-aligned with tabular figures (``className="num"`` on th/td). Below
 * 640px each row becomes a card of labelled lines, unless ``stack`` is off. */
export function Table({
  children,
  size = "md",
  className,
  label,
  fixed,
  layout,
  stack = true,
}: {
  children: React.ReactNode;
  size?: "sm" | "md" | "lg";
  className?: string;
  label?: string;
  /** Respect the widths set on the header cells. */
  fixed?: boolean;
  layout?: "breakdown";
  stack?: boolean;
}) {
  const ref = React.useRef<HTMLTableElement>(null);
  React.useLayoutEffect(() => {
    if (ref.current) labelCells(ref.current);
  });
  return (
    <div className="ds-table-wrap">
      <table
        ref={ref}
        className={`ds-table ${size}${fixed || layout ? " fixed" : ""}${layout ? ` ${layout}` : ""}${stack ? " stack" : ""}${className ? ` ${className}` : ""}`}
        aria-label={label}
      >
        {children}
      </table>
    </div>
  );
}

/** A table row that opens something: the whole row clicks, Enter works. */
export function RowButton({
  onOpen,
  selected,
  label,
  children,
}: {
  onOpen: () => void;
  selected?: boolean;
  label?: string;
  children: React.ReactNode;
}) {
  return (
    <tr
      className={`click${selected ? " is-selected" : ""}`}
      tabIndex={0}
      aria-label={label}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      {children}
    </tr>
  );
}
